/**
 * DashboardView — first surface a signed-in user sees.
 *
 * Wired to the real accounts collection: KPIs, phase funnel, and the
 * recent-activity stream are derived from the same data the kanban
 * and CRM views use, so this is always a live snapshot. Each KPI
 * card is a button that deep-links to the relevant downstream view.
 */

import {
  useEffect,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import {
  ArrowUpRight,
  Bot,
  Building2,
  CalendarClock,
  CheckCircle2,
  Check,
  FolderKanban,
  HeartPulse,
  History,
  Inbox,
  ListChecks,
  Mail,
  Plug,
  Radar,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Workflow,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { runAction, summarizeAction, isUndoableKind, actionRisk } from '../lib/agent/actions';
import { resolveApproval, subscribeApprovals } from '../lib/agent/approvals';
import {
  clampTriageSelection,
  isTypingTarget,
  moveTriageSelection,
} from '../lib/agent/approvalTriage';
import { summarizeValueDelivered } from '../lib/agent/valueLedger';
import { rankCustomersByRisk, type HealthBand } from '../lib/crm/health';
import {
  connectionsStatus,
  countActiveGoals,
  topAtRiskCustomers,
} from '../lib/crm/dashboardPulse';
import { subscribeRuns } from '../lib/agent/orchestrator';
import { workerInfo } from '../lib/agent/argoWorker';
import { slackInfo } from '../lib/agent/slackNotify';
import { proposeFollowUps } from '../lib/agent/followUpAgent';
import { proposeRevenueTasks } from '../lib/agent/revenueAgent';
import { proposeTicketTriage } from '../lib/agent/ticketTriageAgent';
import { proposeTaskRouting } from '../lib/agent/taskRouterAgent';
import { proposeDocumentChases } from '../lib/agent/documentChaseAgent';
import { subscribeAgents } from '../lib/db/agents';
import { GlassPanel } from '../components/GlassPanel';
import { ProposalWhy } from '../components/ProposalWhy';
import { AgentFleetWidget } from '../components/AgentFleetWidget';
import { WorkforceStrip } from '../components/WorkforceStrip';
import { ArgoActivityFeed } from '../components/ArgoActivityFeed';
import { GoalsCardBody } from '../components/GoalsCardBody';
import { ArgoSuggestsStrip } from '../components/ArgoSuggestsStrip';
import { HeartbeatCard } from '../components/HeartbeatCard';
import { DraftFirstControls, NeedsEmailPill } from '../components/DraftFirstControls';
import { proactiveNudges } from '../lib/agent/proactiveNudges';
import { draftForOpsItem } from '../lib/agent/draftFirst';
import { deriveSmartPrompts } from '../lib/crm/smartPrompts';
import { LoadingState } from '../components/States';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  bootstrapAccountsIfEmpty,
  subscribeAccounts,
} from '../lib/db/accounts';
import { bootstrapTicketsIfEmpty, subscribeTickets } from '../lib/db/tickets';
import { bootstrapTasksIfEmpty, subscribeTasks } from '../lib/db/tasks';
import { bootstrapPeopleIfEmpty, subscribePeople } from '../lib/db/people';
import {
  bootstrapDocumentsIfEmpty,
  subscribeDocuments,
} from '../lib/db/documents';
import { subscribeDeliverables } from '../lib/db/deliverables';
import { recordAqiSnapshot, subscribeAqiHistory } from '../lib/db/aqiHistory';
import { subscribeActivity, ACTIVITY_WINDOW_MS } from '../lib/db/activity';
import {
  PIPELINE_STAGES,
  STAGE_PHASE,
  type Account,
  type ApprovalItem,
  type AqiSnapshot,
  type BusinessDoc,
  type Deliverable,
  type DeployedAgent,
  type OrchestratorRun,
  type PipelinePhase,
  type Person,
  type ProjectTask,
  type Ticket,
} from '../lib/db/types';
import {
  scanSystem,
  type FindingSeverity,
  type ScanFinding,
} from '../lib/agent/systemScanner';
import {
  getFollowUpReminders,
  reminderLabel,
  type FollowUpReminder,
} from '../lib/crm/reminders';
import { aqiBand, composeDailyBrief, computeAqi } from '../lib/crm/aqi';
import { assembleWeeklyOps } from '../lib/crm/weeklyOps';
import { buildDoNowQueue, summarizeAqi } from '../lib/crm/homeHero';
import { queueWeeklyDigest } from '../lib/crm/digest';
import type { ViewKey } from '../components/TopMenu';
import type { ProjectsTab } from './ProjectsView';
import type { ActivityEntry } from '../lib/db/types';

interface DashboardViewProps {
  onNavigate?: (key: ViewKey) => void;
  onSelectProjectsTab?: (tab: ProjectsTab) => void;
  /** Hand a query to the chat agent (switches to the Chat view). Gives every
   *  finding/reminder a "let the agent handle it" path, not just a deep-link. */
  onAskArgo?: (query: string) => void;
}

const PHASE_META: Record<
  PipelinePhase,
  { label: string; accent: string; pill: string }
> = {
  sales: {
    label: 'Sales',
    accent: C.magenta,
    pill: 'glass-pill glass-pill--magenta',
  },
  implementations: {
    label: 'Implementations',
    accent: C.blueElectric,
    pill: 'glass-pill glass-pill--blue',
  },
  'customer-success': {
    label: 'Customer Success',
    accent: C.green,
    pill: 'glass-pill glass-pill--green',
  },
};

const SEVERITY_META: Record<
  FindingSeverity,
  { label: string; dot: string; pill: string }
> = {
  high: { label: 'High', dot: C.magenta, pill: 'glass-pill glass-pill--magenta' },
  medium: { label: 'Medium', dot: C.orangeWarm, pill: 'glass-pill glass-pill--orange' },
  low: { label: 'Low', dot: C.blueElectric, pill: 'glass-pill glass-pill--blue' },
};

/**
 * Turn a scanner finding into a chat-agent query. Ticket findings ask for
 * the live status (the local backend can answer those); entity findings ask
 * for the record summary; everything else hands the headline to the agent.
 */
function findingQuery(f: ScanFinding): string {
  const headline = f.title.split('·')[0].trim();
  switch (f.source) {
    case 'ticket': {
      const title = f.title.includes('·')
        ? f.title.split('·').slice(1).join('·').trim()
        : f.title;
      return `What's the status of ${title}?`;
    }
    case 'prospect':
    case 'account':
      return `Tell me about ${headline}`;
    default:
      return `${f.title} — ${f.detail}. What should I do about this?`;
  }
}

/**
 * Unified activity feed item — one shape across account audits, contact
 * touches, ticket events, task events, and agent runs. The `source` chips
 * + the human/agent filter make this the team's shared "what happened"
 * record AND the public audit trail for everything agents do.
 */
interface FeedItem {
  id: string;
  at: string;
  actor: string;
  text: string;
  /** Owning entity, e.g. account name, contact name, ticket id. */
  label: string;
  source: 'account' | 'contact' | 'ticket' | 'task' | 'agent';
}

/** Actors that are agents, e.g. "claude:fixer", "argo", "agent:x". */
const AGENT_ACTOR = /^(claude|argo|agent)\b/i;

const FEED_SOURCE_META: Record<FeedItem['source'], { label: string; pill: string }> = {
  account: { label: 'Account', pill: 'glass-pill glass-pill--blue' },
  contact: { label: 'Contact', pill: 'glass-pill glass-pill--magenta' },
  ticket: { label: 'Ticket', pill: 'glass-pill glass-pill--orange' },
  task: { label: 'Task', pill: 'glass-pill' },
  agent: { label: 'Agent', pill: 'glass-pill glass-pill--green' },
};

/** Plain ISO date n days from today (local). */
function plusDaysIso(n: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Display label for each governance activity event, shown in the feed. */
const ACTIVITY_META: Record<ActivityEntry['event'], string> = {
  proposed: 'Proposed',
  approved: 'Approved',
  executed: 'Executed',
  rejected: 'Rejected',
  undone: 'Undone',
  agent_run: 'Agent run',
};

export function DashboardView({
  onNavigate,
  onSelectProjectsTab,
  onAskArgo,
}: DashboardViewProps) {
  const { user, hasRole } = useAuth();
  const canMutate = hasRole('staff');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [documents, setDocuments] = useState<BusinessDoc[]>([]);
  // First-class deliverables — feed the Autopilot "Do now" hero's overdue-
  // deliverable signal (same source the Customer Success queue reads).
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [agents, setAgents] = useState<DeployedAgent[]>([]);
  const [approvals, setApprovals] = useState<ApprovalItem[]>([]);
  // The full ledger (all statuses) powers the "Value delivered" tally, which
  // counts only approved work. Kept separate from `approvals` (pending-only,
  // drives the inbox) so the inbox UI is unchanged.
  const [allApprovals, setAllApprovals] = useState<ApprovalItem[]>([]);
  const [aqiHistory, setAqiHistory] = useState<AqiSnapshot[]>([]);
  // Orchestrator runs — powers the "Active goals" pulse card so goal runs
  // in flight are visible from home, not only inside the Goals view.
  const [runs, setRuns] = useState<OrchestratorRun[]>([]);
  // The governance activity log (append-only, newest-first). Powers both the
  // honest "Governed activity · 7d" KPI and the recent-activity feed below,
  // replacing the old KPI that counted stale per-account audit rows.
  const [activityRows, setActivityRows] = useState<ActivityEntry[]>([]);
  // First-snapshot flag, flipped once the central CRM data (people) first
  // arrives, so the page shows an honest skeleton instead of a wall of
  // zeros before the store has answered.
  const [loaded, setLoaded] = useState(false);
  // Unified-feed filter: everything / humans only / agents only.
  const [feedFilter, setFeedFilter] = useState<'all' | 'human' | 'agent'>('all');
  // Weekly digest button state.
  const [digestSending, setDigestSending] = useState(false);
  const [digestQueued, setDigestQueued] = useState(false);
  // "Run agents" button state — the real agent loop trigger.
  const [proposing, setProposing] = useState(false);
  const [proposeResult, setProposeResult] = useState<string | null>(null);
  // Approvals triage cockpit — keyboard selection (J/K/E/X) + the
  // Shift+A batch. Selection is a raw index clamped at read time, so a
  // resolved row's removal naturally advances to the next row down.
  const [approvalSelRaw, setApprovalSelRaw] = useState(0);
  const [approvalsBusy, setApprovalsBusy] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    bootstrapAccountsIfEmpty().catch(() => undefined);
    bootstrapTicketsIfEmpty().catch(() => undefined);
    bootstrapTasksIfEmpty().catch(() => undefined);
    bootstrapPeopleIfEmpty().catch(() => undefined);
    bootstrapDocumentsIfEmpty().catch(() => undefined);
    const unsubs = [
      subscribeAccounts((r) => active && setAccounts(r)),
      subscribeTickets((r) => active && setTickets(r)),
      subscribeTasks((r) => active && setTasks(r)),
      subscribePeople((r) => {
        if (!active) return;
        setPeople(r);
        setLoaded(true);
      }),
      subscribeDocuments((r) => active && setDocuments(r)),
      subscribeDeliverables((r) => active && setDeliverables(r)),
      subscribeAgents((r) => active && setAgents(r)),
      subscribeApprovals((r) => {
        if (!active) return;
        setApprovals(r.filter((a) => a.status === 'pending'));
        setAllApprovals(r);
      }),
      subscribeAqiHistory((r) => active && setAqiHistory(r)),
      subscribeActivity((r) => active && setActivityRows(r)),
      subscribeRuns((r) => active && setRuns(r)),
    ];
    return () => {
      active = false;
      unsubs.forEach((u) => u());
    };
  }, []);

  // --- System scan: the surfaced "needs attention" feed -------------------
  const scan = useMemo(
    () => scanSystem({ tickets, tasks, people, accounts, documents }),
    [tickets, tasks, people, accounts, documents],
  );

  // --- Follow-up reminders: overdue + due-soon contacts -------------------
  const reminders = useMemo(() => getFollowUpReminders(people), [people]);
  const reminderCounts = useMemo(() => {
    let overdue = 0;
    let today = 0;
    for (const r of reminders) {
      if (r.urgency === 'overdue') overdue++;
      else if (r.urgency === 'today') today++;
    }
    return { overdue, today, soon: reminders.length - overdue - today };
  }, [reminders]);

  // --- Customer health: live accounts that have slipped -------------------
  // Riskiest-first, healthy accounts dropped — the ones a CS owner should
  // touch today. Scored on demand from each customer's real record, so this
  // can never drift from the source.
  const atRisk = useMemo(
    () =>
      rankCustomersByRisk(people, { tasks }).filter((r) => r.health.band !== 'healthy'),
    [people, tasks],
  );

  // --- Pulse row: surface buried features on home --------------------------
  // (a) the top few slipping customers, (b) goal runs in flight, (c) the
  // honest AI/Slack connection status. Each card deep-links to its view.
  const pulseAtRisk = useMemo(
    () => topAtRiskCustomers(people, { tasks, limit: 3 }),
    [people, tasks],
  );
  const activeGoals = useMemo(() => countActiveGoals(runs), [runs]);

  // --- Argo suggests: the SAME nudge engine the CRM/CS views render -------
  // scanSystem findings + the pure proactiveNudges ranking, so Home and the
  // per-view strips can never disagree about what the agent would suggest.
  const nudges = useMemo(
    () => proactiveNudges({ people, tasks, findings: scan.findings }),
    [people, tasks, scan],
  );

  // --- Smart prompt chips: deterministic, data-derived header prompts ------
  // Pure derivation (deriveSmartPrompts) over counts this view already
  // computes — each chip deep-links to the owning view or prefills Chat.
  const smartPrompts = useMemo(
    () =>
      deriveSmartPrompts({
        overdueFollowUps: reminderCounts.overdue,
        highFindings: scan.findings.filter((f) => f.severity === 'high').length,
        atRiskCustomers: atRisk.length,
      }),
    [reminderCounts, scan, atRisk],
  );
  // Env-derived, so computed once — workerInfo()/slackInfo() read build flags.
  const connections = useMemo(
    () =>
      connectionsStatus({
        aiConfigured: workerInfo().configured,
        slackConfigured: slackInfo().configured,
      }),
    [],
  );

  // --- Unified activity feed: every timeline in one stream ----------------
  const feed = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = [];
    for (const a of accounts)
      for (const e of a.audit)
        items.push({ id: `acct-${a.id}-${e.id}`, at: e.at, actor: e.actor ?? 'You', text: e.text, label: a.name, source: 'account' });
    for (const p of people)
      for (const e of p.activity ?? [])
        items.push({ id: `person-${p.id}-${e.id}`, at: e.at, actor: e.actor ?? 'You', text: e.text, label: p.name, source: 'contact' });
    for (const t of tickets)
      for (const e of t.timeline)
        items.push({ id: `ticket-${t.id}-${e.id}`, at: e.at, actor: e.actor, text: e.text, label: t.id, source: 'ticket' });
    for (const t of tasks)
      for (const e of t.timeline)
        items.push({ id: `task-${t.id}-${e.id}`, at: e.at, actor: e.actor, text: e.text, label: t.title, source: 'task' });
    for (const ag of agents)
      for (const e of ag.timeline)
        items.push({ id: `agent-${ag.id}-${e.id}`, at: e.at, actor: e.actor, text: e.text, label: ag.name, source: 'agent' });
    items.sort((x, y) => (x.at < y.at ? 1 : -1));
    return items;
  }, [accounts, people, tickets, tasks, agents]);

  const visibleFeed = useMemo(() => {
    const filtered =
      feedFilter === 'all'
        ? feed
        : feed.filter((i) =>
            feedFilter === 'agent' ? AGENT_ACTOR.test(i.actor) : !AGENT_ACTOR.test(i.actor),
          );
    return filtered.slice(0, 14);
  }, [feed, feedFilter]);

  // --- AQI + the agent's daily brief ---------------------------------------
  const aqi = useMemo(
    () => computeAqi({ people, accounts, tickets, tasks, agents }),
    [people, accounts, tickets, tasks, agents],
  );
  const brief = useMemo(
    () => composeDailyBrief({ reminders, scan, aqi }),
    [reminders, scan, aqi],
  );
  // Plain-language translation of the score — what it means, what to do.
  const aqiSummary = useMemo(() => summarizeAqi(aqi), [aqi]);

  // --- Autopilot "This week / Do now" hero ---------------------------------
  // The ranked cross-account ops queue (assembleWeeklyOps), dressed with a
  // verb-first one-click action per item. Recomputed from the live
  // snapshots, so opening Home always answers "what do I do next?".
  const weeklyOps = useMemo(
    () =>
      assembleWeeklyOps({
        people,
        accounts,
        tasks,
        tickets,
        deliverables,
        findings: scan.findings,
      }),
    [people, accounts, tasks, tickets, deliverables, scan],
  );
  const doNow = useMemo(() => buildDoNowQueue(weeklyOps), [weeklyOps]);
  // Draft-first Do-now: [Skip]-dismissed row ids (session-local, queues
  // nothing). Rows whose record composes into a finished email carry the
  // artifact + [Approve & send] instead of a go-do-it-yourself deep link.
  const [doNowSkipped, setDoNowSkipped] = useState<Record<string, boolean>>({});
  const doNowVisible = useMemo(
    () => doNow.filter((row) => !doNowSkipped[row.item.id]),
    [doNow, doNowSkipped],
  );

  // --- Value delivered: the honest tally of approved agent work -----------
  // Reads the whole ledger, counts only approved items. This is the seed of
  // outcome-metering and the proof artifact a customer asks for.
  const value = useMemo(
    () => summarizeValueDelivered(allApprovals),
    [allApprovals],
  );

  // Persist today's AQI so the dial can show a trend. Idempotent per day
  // (latest reading wins), fire-and-forget, keyed on the score so an
  // unchanged number never re-writes.
  useEffect(() => {
    void recordAqiSnapshot(aqi);
  }, [aqi.score]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Derived KPIs --------------------------------------------------------

  const stats = useMemo(() => {
    let workflowsTotal = 0;
    let inSales = 0;
    let inImpl = 0;
    let inCS = 0;

    for (const a of accounts) {
      workflowsTotal += a.workflows.length;
      for (const wf of a.workflows) {
        const phase = STAGE_PHASE[wf.stage];
        if (phase === 'sales') inSales++;
        else if (phase === 'implementations') inImpl++;
        else if (phase === 'customer-success') inCS++;
      }
    }

    return {
      accountsCount: accounts.length,
      workflowsTotal,
      inSales,
      inImpl,
      inCS,
    };
  }, [accounts]);

  // Governed activity within the trailing window — the honest KPI value.
  const activityCount = useMemo(() => {
    const cutoff = Date.now() - ACTIVITY_WINDOW_MS;
    return activityRows.filter((e) => Date.parse(e.at) >= cutoff).length;
  }, [activityRows]);

  // --- KPI card definitions -----------------------------------------------

  type KpiTarget = { view: ViewKey; tab?: ProjectsTab };
  interface KpiDef {
    id: string;
    label: string;
    value: number | string;
    sub: string;
    icon: LucideIcon;
    target: KpiTarget;
  }

  const kpis: KpiDef[] = [
    {
      id: 'accounts',
      label: 'Active accounts',
      value: stats.accountsCount,
      sub: `${stats.workflowsTotal} workflows in flight`,
      icon: Building2,
      target: { view: 'contacts' },
    },
    {
      id: 'workflows',
      label: 'Workflows in flight',
      value: stats.workflowsTotal,
      sub: `${stats.inSales} sales · ${stats.inImpl} impl · ${stats.inCS} CS`,
      icon: Workflow,
      target: { view: 'projects', tab: 'projects' },
    },
    {
      id: 'cs',
      label: 'In Customer Success',
      value: stats.inCS,
      sub: 'Most stable cohort — protect it',
      icon: CheckCircle2,
      target: { view: 'projects', tab: 'projects' },
    },
    {
      id: 'audit',
      label: 'Governed activity · 7d',
      value: activityCount,
      sub: 'Approvals, executions & agent runs',
      icon: History,
      target: { view: 'contacts' },
    },
  ];

  const onKpiClick = (target: KpiTarget) => {
    if (target.tab && onSelectProjectsTab) onSelectProjectsTab(target.tab);
    onNavigate?.(target.view);
  };

  // Deep-link a finding back to the view that owns the underlying record.
  const onFindingClick = (f: ScanFinding) => {
    switch (f.source) {
      case 'ticket':
        onNavigate?.('tickets');
        break;
      case 'task':
        onSelectProjectsTab?.('tasks');
        onNavigate?.('projects');
        break;
      case 'prospect':
      case 'account':
        onNavigate?.('contacts');
        break;
      case 'document':
        onNavigate?.('contracts');
        break;
    }
  };

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return 'Late night';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    if (h < 21) return 'Good evening';
    return 'Good evening';
  }, []);

  // Queue the weekly digest email and notify Slack.
  const onEmailWeeklyDigest = async () => {
    if (!user?.email || digestSending) return;
    setDigestSending(true);
    try {
      await queueWeeklyDigest(user.email, { people, accounts, tickets, tasks, agents });
      setDigestQueued(true);
      setTimeout(() => setDigestQueued(false), 3000);
    } finally {
      setDigestSending(false);
    }
  };

  // Run all five real producers in one operator action: each scans live data
  // and queues real proposals into the approvals inbox below — drifting
  // prospects → schedule_follow_up (follow-up agent), pressing revenue events →
  // create_task (revenue agent), overdue unstarted tickets → set_ticket_status
  // (ticket worker), unowned priority tickets → assign_ticket (task router), and
  // lapsed / awaiting documents → create_task chase (document-chase agent).
  // Nothing writes here; approving a card in the inbox runs the real, undoable
  // store write. Run sequentially so the producers' shared pending-inbox de-dupe
  // sees each other's freshly-queued proposals (no double-queueing in one click).
  const onRunAgents = async () => {
    if (proposing) return;
    setProposing(true);
    setProposeResult(null);
    try {
      let proposed = 0;
      let skipped = 0;
      for (const r of [
        await proposeFollowUps(people),
        await proposeRevenueTasks(people),
        await proposeTicketTriage(tickets),
        await proposeTaskRouting(tickets),
        await proposeDocumentChases(documents),
      ]) {
        proposed += r.proposed;
        skipped += r.skipped;
      }
      const parts: string[] = [];
      if (proposed > 0) parts.push(`${proposed} proposed`);
      if (skipped > 0) parts.push(`${skipped} already queued`);
      setProposeResult(
        parts.length > 0 ? parts.join(' · ') : 'Nothing pressing — all clear',
      );
      setTimeout(() => setProposeResult(null), 4000);
    } finally {
      setProposing(false);
    }
  };

  // --- Approvals triage cockpit -------------------------------------------
  // Keyboard-first clearing of the inbox: J/K move, E/Enter approve, X
  // dismiss, Shift+A approves every reversible ('auto' risk) proposal in
  // sequence, leaving gated ones for manual review. All writes go through
  // the same resolveApproval the mouse buttons use.
  const approvalSel = clampTriageSelection(approvalSelRaw, approvals.length);
  const reversibleApprovals = useMemo(
    () => approvals.filter((a) => actionRisk(a.action) === 'auto'),
    [approvals],
  );

  const resolveSelectedApproval = async (decision: 'approved' | 'dismissed') => {
    if (!canMutate || approvalsBusy) return;
    const target = approvals[approvalSel];
    if (!target) return;
    setApprovalError(null);
    try {
      await resolveApproval(target.id, decision);
    } catch (err) {
      setApprovalError(err instanceof Error ? err.message : String(err));
    }
  };

  const onApproveAllReversible = async () => {
    if (!canMutate || approvalsBusy || reversibleApprovals.length === 0) return;
    setApprovalsBusy(true);
    setApprovalError(null);
    try {
      // Sequential on purpose — same discipline as 'Approve all', so each
      // action commits (and logs) before the next runs.
      for (const a of reversibleApprovals) {
        await resolveApproval(a.id, 'approved');
      }
    } catch (err) {
      setApprovalError(err instanceof Error ? err.message : String(err));
    } finally {
      setApprovalsBusy(false);
    }
  };

  useEffect(() => {
    if (approvals.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      // Never steal keys from text entry, palette/modal overlays, or chords
      // that belong to the browser (⌘K etc.). Shift is only meaningful to ⇧A.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"]')) return;
      switch (e.key) {
        case 'j':
        case 'J':
        case 'ArrowDown':
          e.preventDefault();
          setApprovalSelRaw((i) =>
            moveTriageSelection(clampTriageSelection(i, approvals.length), approvals.length, 1),
          );
          break;
        case 'k':
        case 'K':
        case 'ArrowUp':
          e.preventDefault();
          setApprovalSelRaw((i) =>
            moveTriageSelection(clampTriageSelection(i, approvals.length), approvals.length, -1),
          );
          break;
        case 'Enter': {
          // A focused button/link owns its own Enter — don't hijack it.
          const tag = (e.target as HTMLElement | null)?.tagName;
          if (tag === 'BUTTON' || tag === 'A') return;
          e.preventDefault();
          void resolveSelectedApproval('approved');
          break;
        }
        case 'e':
        case 'E':
          e.preventDefault();
          void resolveSelectedApproval('approved');
          break;
        case 'x':
        case 'X':
          e.preventDefault();
          void resolveSelectedApproval('dismissed');
          break;
        case 'A':
          if (e.shiftKey) {
            e.preventDefault();
            void onApproveAllReversible();
          }
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvals, approvalSel, canMutate, approvalsBusy, reversibleApprovals]);

  // Honest first-paint: show the header + a skeleton until the central CRM
  // data has answered. Placed after all hooks above, so this conditional
  // return never changes hook order.
  if (!loaded) {
    return (
      <div className="app-container" style={{ display: 'grid', gap: 18 }}>
        <header>
          <span className="t-eyebrow">Dashboard</span>
          <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
            {greeting}
            {user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}.
          </h1>
          <p style={{ margin: '4px 0 0', color: C.textMuted }} className="t-body">
            Bringing your day into focus…
          </p>
        </header>
        <LoadingState rows={6} label="Loading your dashboard" />
      </div>
    );
  }

  return (
    <div className="app-container" style={{ display: 'grid', gap: 18 }}>
      {/* Header */}
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <span className="t-eyebrow">Dashboard</span>
          <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
            {greeting}
            {user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}.
          </h1>
          <p style={{ margin: '4px 0 0', color: C.textMuted }} className="t-body">
            Where to spend your day, at a glance.
          </p>
          {/* Smart prompts — deterministic chips from live signals. 'ask'
              chips prefill Chat (hidden when no onAskArgo); 'navigate'
              chips deep-link to the view that owns the signal. */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {smartPrompts.map((p) =>
              p.kind === 'ask' ? (
                onAskArgo ? (
                  <button
                    key={p.id}
                    type="button"
                    className="glass-btn"
                    onClick={() => onAskArgo(p.query)}
                    title="Opens Chat with this question prefilled"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}
                  >
                    <Sparkles size={11} aria-hidden="true" /> {p.label}
                  </button>
                ) : null
              ) : (
                <button
                  key={p.id}
                  type="button"
                  className="glass-btn"
                  onClick={() => onNavigate?.(p.view)}
                  title={`Open ${p.view.replace('-', ' ')}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}
                >
                  {p.label} <ArrowUpRight size={11} aria-hidden="true" />
                </button>
              ),
            )}
          </div>
        </div>
        <button
          className="glass-btn glass-btn--primary"
          onClick={() => {
            onSelectProjectsTab?.('tasks');
            onNavigate?.('projects');
          }}
        >
          <ListChecks size={13} /> Tasks
        </button>
      </header>

      {/* Autopilot: This week / Do now — the verb-first hero. The ranked
          cross-account ops queue leads the page, so opening Home tells you
          exactly what to do next; KPIs and the scoreboard follow. Every row
          is a real signal from the ranking engine with a one-click action
          (deep-link) and an optional "hand it to Argo" path. */}
      <GlassPanel variant="default" style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: doNow.length > 0 ? 12 : 6, flexWrap: 'wrap' }}>
          <Zap size={14} color={C.orangeWarm} aria-hidden="true" />
          <span className="t-section-title" style={{ margin: 0 }}>This week · Do now</span>
          <span
            className="glass-pill"
            style={{ fontSize: 10, padding: '2px 8px' }}
            title="Ranked by transparent priority: at-risk accounts, high findings, overdue deliverables and follow-ups, then weekly updates due."
          >
            {weeklyOps.length} in queue
          </span>
          <span style={{ flex: 1 }} />
          <button
            className="glass-btn"
            onClick={() => onNavigate?.('customer-success')}
            title="Open the full ranked ops queue in Customer Success"
          >
            Full queue <ArrowUpRight size={12} aria-hidden="true" />
          </button>
        </div>
        {doNowVisible.length === 0 ? (
          <p className="t-meta" style={{ margin: 0, fontSize: 12 }}>
            Nothing pressing this week — no at-risk accounts, overdue
            follow-ups, late deliverables, or high-severity findings. New work
            appears here the moment a signal fires.
          </p>
        ) : (
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
            {doNowVisible.map((row, i) => {
              // Draft-first: when this row's record composes into a finished
              // email (draftForOpsItem — real data only), the card carries
              // the artifact + [Approve & send] [Edit] [Skip] and the old
              // go-do-it-yourself CTA is removed. Null → honest fallback to
              // the deep link; needs-email → the honest no-address state.
              const draftState = canMutate ? draftForOpsItem(row.item, people) : null;
              return (
              <li
                key={row.item.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(0,0,0,0.20)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span
                  aria-hidden="true"
                  style={{
                    flex: '0 0 auto',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 11,
                    fontWeight: 800,
                    color: i === 0 ? C.orangeWarm : C.textMuted,
                    border: '1px solid rgba(255,255,255,0.14)',
                  }}
                >
                  {i + 1}
                </span>
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <div className="t-card-title" style={{ fontSize: 13 }}>{row.item.title}</div>
                  <div className="t-meta" style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                    {row.item.reason}
                  </div>
                </div>
                <span
                  className="glass-pill"
                  style={{ fontSize: 10, padding: '2px 8px', flex: '0 0 auto' }}
                  title="Transparent priority: fixed base per signal kind plus a bounded magnitude (days overdue, health deficit, blockers)."
                >
                  P{row.item.priorityScore}
                </span>
                <div style={{ display: 'flex', gap: 6, flex: '0 0 auto', alignItems: 'center' }}>
                  {draftState?.state === 'needs-email' && (
                    <NeedsEmailPill personName={draftState.personName} />
                  )}
                  {draftState?.state !== 'draft' && (
                    <button
                      className="glass-btn glass-btn--primary"
                      onClick={() => onNavigate?.(row.targetView)}
                      title={`Open ${row.targetView.replace('-', ' ')} to act on this`}
                    >
                      {row.actionLabel}
                    </button>
                  )}
                  {onAskArgo && (
                    <button
                      className="glass-btn"
                      onClick={() => onAskArgo(row.askQuery)}
                      title="Hand this item to the agent"
                    >
                      <Sparkles size={12} aria-hidden="true" /> Ask Argo
                    </button>
                  )}
                </div>
                </div>
                {draftState?.state === 'draft' && (
                  <DraftFirstControls
                    draft={draftState}
                    note={row.item.reason}
                    onSkip={() =>
                      setDoNowSkipped((s) => ({ ...s, [row.item.id]: true }))
                    }
                  />
                )}
              </li>
              );
            })}
          </ol>
        )}
      </GlassPanel>

      {/* Your AI workforce — one card per deployed agent: what it does in
          plain English, honest last-run time, pending proposals, and a
          governed "Run now". Deep-links to the full Agents view. */}
      <WorkforceStrip
        canMutate={canMutate}
        onOpenAgents={() => {
          onSelectProjectsTab?.('agents');
          onNavigate?.('projects');
        }}
      />

      {/* Argo has been working — one honest line per REAL agent run (live
          schedule or Run-now), with real counts and a jump to Approvals
          when a run queued proposals. Empty state: "No runs yet today." */}
      <ArgoActivityFeed
        onOpenApprovals={() =>
          document
            .getElementById('dash-approvals-inbox')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      />

      {/* Heartbeat — at most ONE "approve once, runs on autopilot" proposal
          per day, derived from real observed signals (past-due invoices,
          slipped follow-ups, weekly-update cadence). Approving turns on
          exactly one scoped, pausable automation that only QUEUES drafts
          for approval — never a global autonomy switch. */}
      <HeartbeatCard people={people} accounts={accounts} canMutate={canMutate} />

      {/* Argo suggests — the SAME shared nudge source the CRM/CS views
          render. Every chip queues a governed proposal into the approvals
          inbox below; nothing auto-runs. Renders nothing when the book is
          clean (honest quiet, no filler). */}
      <ArgoSuggestsStrip nudges={nudges} canMutate={canMutate} people={people} />

      {/* KPIs — the numbers, AFTER the verbs: the hero above says what to
          do; these cards say how much of everything there is. Each one
          deep-links to the view that owns it. */}
      <div className="dash-kpis">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <button
              key={k.id}
              type="button"
              className="dash-kpi"
              onClick={() => onKpiClick(k.target)}
            >
              <span className="dash-kpi__icon" aria-hidden="true">
                <Icon size={16} strokeWidth={2} />
              </span>
              <span className="dash-kpi__value">{k.value}</span>
              <span className="dash-kpi__label">{k.label}</span>
              <span className="dash-kpi__sub">{k.sub}</span>
              <ArrowUpRight size={12} className="dash-kpi__chev" aria-hidden="true" />
            </button>
          );
        })}
      </div>

      {/* Morning briefing — the AQI scoreboard + the agent's daily brief.
          AQI is the north star made visible: five transparent components,
          each with a receipt, so the score is a lever rather than vanity. */}
      <GlassPanel
        variant="default"
        style={{ padding: '18px 20px', display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}
      >
        {(() => {
          const band = aqiBand(aqi.score);
          const accent = band === 'strong' ? C.green : band === 'steady' ? C.blueElectric : C.orangeWarm;
          return (
            <div
              title={`${aqiSummary.advice}\n\n${aqi.components.map((c) => `${c.label}: ${c.score} — ${c.detail}`).join('\n')}`}
              style={{
                flex: '0 0 auto',
                width: 96,
                height: 96,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                background: `conic-gradient(${accent} ${aqi.score * 3.6}deg, rgba(255,255,255,0.08) 0deg)`,
              }}
              aria-label={`Ops health score ${aqi.score} of 100 — ${band}. ${aqiSummary.advice}`}
            >
              <div
                style={{
                  width: 78,
                  height: 78,
                  borderRadius: '50%',
                  background: 'rgba(10,8,18,0.92)',
                  display: 'grid',
                  placeItems: 'center',
                  textAlign: 'center',
                }}
              >
                <div>
                  <div className="t-page-title" style={{ margin: 0, fontSize: 26, lineHeight: 1 }}>
                    {aqi.score}
                  </div>
                  <div className="t-meta" style={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: 9 }}>
                    Ops health
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        {/* Plain-language translation of the dial — what the score means
            and the single most effective thing to do about it, so "29"
            never stands alone. */}
        <div style={{ flex: '0 0 auto', maxWidth: 210 }}>
          <div
            className="t-card-title"
            style={{ fontSize: 12, textTransform: 'capitalize' }}
          >
            {aqiSummary.label}
          </div>
          <p className="t-meta" style={{ margin: '4px 0 0', fontSize: 11, color: C.textMuted }}>
            {aqiSummary.advice}
          </p>
        </div>
        {/* AQI trend — one honest point per day from the aqiHistory store.
            Sparse-state aware: until 2 days are logged it says so rather
            than faking a line. */}
        {(() => {
          const pts = aqiHistory.slice(-14);
          if (pts.length < 2) {
            return (
              <div style={{ flex: '0 0 auto', minWidth: 120 }}>
                <div className="t-meta" style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Health trend
                </div>
                <div className="t-meta" style={{ color: C.textMuted, marginTop: 6, maxWidth: 130 }}>
                  {pts.length === 0 ? 'First reading logged today.' : '1 day logged — trend builds daily.'}
                </div>
              </div>
            );
          }
          const scores = pts.map((p) => p.score);
          const min = Math.min(...scores);
          const max = Math.max(...scores);
          const span = max - min || 1;
          const W = 132;
          const H = 38;
          const PAD = 3;
          const stepX = (W - PAD * 2) / (pts.length - 1);
          const coords = scores.map((s, i) => {
            const x = PAD + i * stepX;
            const y = PAD + (H - PAD * 2) * (1 - (s - min) / span);
            return [x, y] as const;
          });
          const path = coords
            .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
            .join(' ');
          const [lastX, lastY] = coords[coords.length - 1];
          const delta = scores[scores.length - 1] - scores[0];
          const trendBand = aqiBand(aqi.score);
          const stroke = trendBand === 'strong' ? C.green : trendBand === 'steady' ? C.blueElectric : C.orangeWarm;
          const deltaColor = delta > 0 ? C.green : delta < 0 ? C.orangeWarm : C.textMuted;
          return (
            <div style={{ flex: '0 0 auto', minWidth: 132 }}>
              <div className="t-meta" style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                Health trend · {pts.length}d
              </div>
              <svg
                width={W}
                height={H}
                viewBox={`0 0 ${W} ${H}`}
                role="img"
                aria-label={`AQI trend over the last ${pts.length} days, ${delta >= 0 ? 'up' : 'down'} ${Math.abs(delta)} points`}
                style={{ display: 'block' }}
              >
                <path d={path} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
                <circle cx={lastX} cy={lastY} r={2.5} fill={stroke} />
              </svg>
              <div className="t-meta" style={{ color: deltaColor, marginTop: 2, fontSize: 11 }}>
                {delta > 0 ? '▲' : delta < 0 ? '▼' : '■'} {Math.abs(delta)} vs {pts.length}d ago
              </div>
            </div>
          );
        })()}
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Bot size={14} color={C.magentaSoft} aria-hidden="true" />
            <span className="t-eyebrow">Daily brief</span>
          </div>
          <p className="t-body" style={{ margin: 0, color: C.text }}>{brief}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {onAskArgo && (
              <button
                className="glass-btn glass-btn--primary"
                onClick={() => onAskArgo('What needs my attention today?')}
                title="Open the agent with today's work loaded"
              >
                <Sparkles size={13} /> Work it with Argo
              </button>
            )}
            {/* Write affordance — queues an outbox doc, so editors only.
                Viewers see the briefing read-only (hide, don't error). */}
            {canMutate && (
              <button
                className="glass-btn"
                onClick={() => void onEmailWeeklyDigest()}
                disabled={digestSending || !user?.email}
                title={user?.email ? 'Writes to the outbox collection — delivered by the sendEmail Cloud Function once deployed (simulated locally).' : 'Sign in to send digest'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <Mail size={13} /> {digestQueued ? 'Queued ✓' : digestSending ? '…' : 'Email weekly digest'}
              </button>
            )}
            {aqi.components.map((c) => (
              <span key={c.key} className="glass-pill" title={c.detail} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {c.label}
                <span style={{ fontWeight: 700, color: c.score >= 80 ? C.green : c.score >= 55 ? C.blueElectric : C.orangeWarm }}>
                  {c.score}
                </span>
              </span>
            ))}
          </div>
        </div>
      </GlassPanel>

      {/* Value delivered — the honest tally of approved agent work. The same
          ledger that gates trust (human approval) is the metering surface: every
          number here is a real, approved, logged action. Estimates are labelled
          as such; dollars are "influenced" (touched), never "recovered". */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <ShieldCheck size={14} />
          <span className="t-section-title" style={{ margin: 0 }}>Value delivered</span>
          <span
            className="glass-pill"
            style={{ fontSize: 10, padding: '2px 8px' }}
            title="Counts only actions an agent proposed and a human approved — proposals and dismissed items are excluded."
          >
            approved work
          </span>
        </div>
        {value.approvedCount === 0 ? (
          <p className="t-meta" style={{ margin: 0, fontSize: 12 }}>
            No approved agent work yet. When you approve an action from the queue
            below, its value shows up here — real, logged, reversible.
          </p>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 10,
              }}
            >
              {[
                {
                  label: 'Approved actions',
                  value: String(value.approvedCount),
                  sub: 'agent work, human-signed',
                },
                {
                  label: 'Operator time saved',
                  value: `${value.hoursSaved}h`,
                  sub: `${value.minutesSaved} min · est.`,
                },
                {
                  label: 'Dollars influenced',
                  value:
                    value.dollarsInfluenced > 0
                      ? value.dollarsInfluenced.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          maximumFractionDigits: 0,
                        })
                      : '—',
                  sub: 'touched by approved actions',
                },
              ].map((tile) => (
                <div
                  key={tile.label}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.08)',
                    background: 'rgba(0,0,0,0.20)',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      fontWeight: 800,
                      fontSize: 22,
                      lineHeight: 1.1,
                      color: C.magentaSoft,
                    }}
                  >
                    {tile.value}
                  </span>
                  <span className="t-card-title" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                    {tile.label}
                  </span>
                  <span className="t-meta" style={{ display: 'block', fontSize: 10 }}>
                    {tile.sub}
                  </span>
                </div>
              ))}
            </div>
            <p className="t-meta" style={{ margin: '10px 0 0', fontSize: 10, opacity: 0.7 }}>
              Time saved is a transparent per-kind estimate (the manual work an
              approval replaces). Dollars are figures the approved actions touched,
              not cash banked.
            </p>
          </>
        )}
      </GlassPanel>

      {/* Recent governed activity — the append-only audit trail made visible.
          Every proposal, approval, rejection, execution, and agent run, newest
          first. This turns the governance moat into a control the operator can
          see, not just a promise. Honestly empty until something happens. */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <History size={14} />
          <span className="t-section-title" style={{ margin: 0 }}>Recent governed activity</span>
          {activityRows.length > 0 && (
            <span className="glass-pill" style={{ fontSize: 10, padding: '2px 8px' }}>
              {activityCount} in 7d
            </span>
          )}
        </div>
        {activityRows.length === 0 ? (
          <p className="t-meta" style={{ margin: 0, fontSize: 12 }}>
            No governed activity yet. Proposals, approvals, rejections, executions,
            and agent runs are recorded here — an append-only trail, never seeded.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activityRows.slice(0, 6).map((e) => (
              <div
                key={e.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <span
                  className="glass-pill"
                  style={{ fontSize: 10, padding: '2px 8px', flexShrink: 0 }}
                >
                  {ACTIVITY_META[e.event]}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 12,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {e.summary}
                </span>
                <span className="t-meta" style={{ fontSize: 11, flexShrink: 0 }}>
                  {fmtRelative(e.at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>

      {/* Approvals inbox — proposals from schedulers and autopilot. */}
      {approvals.length > 0 && (
        <GlassPanel
          id="dash-approvals-inbox"
          variant="subtle"
          animate={false}
          style={{ padding: '14px 16px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Inbox size={14} />
              <span className="t-section-title" style={{ margin: 0 }}>Approvals</span>
              <span
                className="glass-pill"
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                {approvals.length}
              </span>
            </div>
            {/* Approve/Dismiss are writes (resolveApproval). Viewers see the
                queue read-only — affordances hidden, not errored. */}
            {canMutate ? (
              <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                  className="glass-btn"
                  style={{ padding: '6px 12px', fontSize: 12 }}
                  onClick={onApproveAllReversible}
                  disabled={approvalsBusy || reversibleApprovals.length === 0}
                  title="Approve every reversible (undoable) proposal — gated ones stay for manual review (⇧A)"
                >
                  <Check size={12} /> Approve all reversible ({reversibleApprovals.length})
                </button>
                <button
                  className="glass-btn glass-btn--primary"
                  style={{ padding: '6px 12px', fontSize: 12 }}
                  onClick={async () => {
                    for (const a of approvals) {
                      await resolveApproval(a.id, 'approved');
                    }
                  }}
                  disabled={approvalsBusy}
                  title="Approve all pending actions"
                >
                  <Check size={12} /> Approve all
                </button>
              </span>
            ) : (
              <span className="glass-pill" title="Read-only access — editors only" style={{ fontSize: 11 }}>
                🔒 View only
              </span>
            )}
          </div>
          {canMutate && (
            <p className="t-meta" style={{ margin: '-6px 0 10px', fontSize: 10, opacity: 0.7 }}>
              J/K move · E approve · X dismiss · ⇧A approve all reversible
            </p>
          )}
          {approvalError && (
            <p className="t-meta" style={{ margin: '0 0 10px', fontSize: 11, color: C.magentaSoft }}>
              {approvalError}
            </p>
          )}
          <div style={{ display: 'grid', gap: 8 }}>
            {approvals.map((approval, idx) => {
              const { verb, detail } = summarizeAction(approval.action);
              const selected = idx === approvalSel;
              return (
                <div
                  key={approval.id}
                  onClick={() => setApprovalSelRaw(idx)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: selected
                      ? '1px solid rgba(255,0,255,0.35)'
                      : '1px solid rgba(255,255,255,0.08)',
                    background: selected ? 'rgba(255,0,255,0.06)' : 'rgba(0,0,0,0.20)',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="t-card-title" style={{ display: 'block', color: C.magentaSoft, fontWeight: 700, fontSize: 12 }}>
                      {verb}
                    </span>
                    <span className="t-meta" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11 }}>
                      {detail}
                    </span>
                    <span
                      className="glass-pill"
                      style={{
                        display: 'inline-block',
                        marginTop: 4,
                        fontSize: 9,
                        padding: '2px 6px',
                        background: 'rgba(76,209,223,0.08)',
                        border: '1px solid rgba(76,209,223,0.18)',
                      }}
                    >
                      {approval.source}
                    </span>
                    {/* Reversibility, shown BEFORE approval so the operator
                        knows whether an approved action can be undone — honest
                        about the "best-effort undo" limit rather than hiding it. */}
                    {isUndoableKind(approval.action.kind) ? (
                      <span
                        className="glass-pill"
                        title="This action can be undone after approval (best-effort, session-local)."
                        style={{
                          display: 'inline-block',
                          marginTop: 4,
                          marginLeft: 6,
                          fontSize: 9,
                          padding: '2px 6px',
                          background: 'rgba(110,231,183,0.10)',
                          border: '1px solid rgba(110,231,183,0.22)',
                        }}
                      >
                        Reversible
                      </span>
                    ) : (
                      <span
                        className="glass-pill"
                        title="This action cannot be automatically undone — review carefully before approving."
                        style={{
                          display: 'inline-block',
                          marginTop: 4,
                          marginLeft: 6,
                          fontSize: 9,
                          padding: '2px 6px',
                          background: 'rgba(252,211,77,0.10)',
                          border: '1px solid rgba(252,211,77,0.25)',
                        }}
                      >
                        Not reversible
                      </span>
                    )}
                    {/* Expandable "Why?" — rationale + evidence bullets the
                        proposer attached. Renders nothing when absent, so
                        older proposals are untouched. */}
                    <ProposalWhy
                      rationale={approval.rationale}
                      evidence={approval.evidence}
                    />
                  </span>
                  {canMutate && (
                    <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        className="glass-btn"
                        onClick={() => resolveApproval(approval.id, 'dismissed')}
                        title="Dismiss"
                        style={{ padding: '4px 6px' }}
                      >
                        <X size={12} />
                      </button>
                      <button
                        className="glass-btn glass-btn--primary"
                        onClick={() => resolveApproval(approval.id, 'approved')}
                        title="Approve and run"
                        style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Check size={12} /> Approve
                      </button>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </GlassPanel>
      )}

      {/* Pulse row — health, goals & connections surfaced on home. Each
          card is a real deep-link: Customer Success, Goals, and Tools
          (where the worker seams live). */}
      <div
        className="dash-pulse"
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        }}
      >
        <PulseCard
          icon={HeartPulse}
          title="At-risk customers"
          cta="Customer Success"
          accent={pulseAtRisk.some((r) => r.health.band === 'at-risk') ? C.magenta : C.green}
          onClick={() => onNavigate?.('customer-success')}
          aria-label="At-risk customers — open Customer Success"
        >
          {pulseAtRisk.length === 0 ? (
            <span className="t-meta" style={{ color: C.textMuted }}>
              No live customers slipping right now.
            </span>
          ) : (
            <span style={{ display: 'grid', gap: 6 }}>
              {pulseAtRisk.map(({ person, health }) => (
                <span
                  key={person.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}
                >
                  <span
                    className="t-card-title"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {person.name}
                    {person.company ? ` · ${person.company}` : ''}
                  </span>
                  <span
                    className={
                      health.band === 'at-risk'
                        ? 'glass-pill glass-pill--magenta'
                        : 'glass-pill glass-pill--orange'
                    }
                    style={{ flex: '0 0 auto' }}
                  >
                    {health.score}/100
                  </span>
                </span>
              ))}
            </span>
          )}
        </PulseCard>

        <PulseCard
          icon={Target}
          title="Active goals"
          cta="Goals"
          accent={activeGoals > 0 ? C.blueElectric : C.textDim}
          onClick={() => onNavigate?.('goals')}
          aria-label="Active goals — open Goals"
        >
          {/* Compact goals rollup — active goals, runs in flight, and the
              next milestone from the live runs; honest "No goals yet — set
              one" zero state. The whole card clicks through to Goals. */}
          <GoalsCardBody runs={runs} />
        </PulseCard>

        <PulseCard
          icon={Plug}
          title="Connections"
          cta="Tools"
          accent={connections.aiConnected ? C.green : C.blueElectric}
          onClick={() => onNavigate?.('tools')}
          aria-label="Connections — open Tools"
        >
          <span style={{ display: 'grid', gap: 6 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: connections.aiConnected ? C.green : C.blueElectric,
                  boxShadow: `0 0 6px ${connections.aiConnected ? C.green : C.blueElectric}`,
                }}
              />
              <span className="t-meta">AI · {connections.ai}</span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: connections.slackConnected ? C.green : C.blueElectric,
                  boxShadow: `0 0 6px ${connections.slackConnected ? C.green : C.blueElectric}`,
                }}
              />
              <span className="t-meta">Slack · {connections.slack}</span>
            </span>
          </span>
        </PulseCard>
      </div>

      {/* Needs attention — system scanner output */}
      <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
        <div className="dash-section-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                display: 'inline-grid',
                placeItems: 'center',
                background: scan.summary.high
                  ? 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))'
                  : 'linear-gradient(135deg, rgba(0,200,120,0.20), rgba(0,130,243,0.10))',
                border: '1px solid rgba(255,255,255,0.14)',
                color: scan.summary.high ? C.magenta : C.green,
              }}
            >
              {scan.summary.total > 0 ? (
                <Radar size={17} strokeWidth={2} />
              ) : (
                <ShieldCheck size={17} strokeWidth={2} />
              )}
            </span>
            <div>
              <span className="t-eyebrow">System scanner</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                Needs attention
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* Real agent trigger — runs all five producers (follow-ups,
                revenue, ticket triage, task routing, document chase) and queues
                their real proposals into the approvals inbox below. Editors
                only, since approving a proposal writes to the store. */}
            {canMutate && (
              <button
                className="glass-btn"
                onClick={() => void onRunAgents()}
                disabled={proposing}
                title="Runs all five agents — follow-ups, revenue, ticket triage, task routing, and document chase — and queues their real proposals into the approvals inbox. Approve there to write the change."
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <Bot size={13} />{' '}
                {proposeResult
                  ? proposeResult
                  : proposing
                    ? 'Running agents…'
                    : 'Run agents'}
              </button>
            )}
            {scan.summary.high > 0 && (
              <span className="glass-pill glass-pill--magenta">
                {scan.summary.high} high
              </span>
            )}
            {scan.summary.medium > 0 && (
              <span className="glass-pill glass-pill--orange">
                {scan.summary.medium} medium
              </span>
            )}
            {scan.summary.low > 0 && (
              <span className="glass-pill glass-pill--blue">
                {scan.summary.low} low
              </span>
            )}
          </div>
        </div>

        {scan.findings.length === 0 ? (
          <div className="crm-empty" style={{ padding: '20px 0' }}>
            <ShieldCheck size={20} strokeWidth={1.75} />
            <span>All clear — nothing overdue, blocked, or stalled.</span>
          </div>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: '4px 0 0',
              padding: 0,
              display: 'grid',
              gap: 8,
            }}
          >
            {scan.findings.slice(0, 8).map((f) => {
              const meta = SEVERITY_META[f.severity];
              return (
                <li key={f.id} style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
                  <button
                    type="button"
                    className="glass-row"
                    onClick={() => onFindingClick(f)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      title={meta.label}
                      style={{
                        flex: '0 0 auto',
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: meta.dot,
                        boxShadow: `0 0 8px ${meta.dot}`,
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        className="t-card-title"
                        style={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {f.title}
                      </span>
                      <span
                        className="t-meta"
                        style={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {f.detail}
                      </span>
                    </span>
                    <span className={meta.pill} style={{ flex: '0 0 auto' }}>
                      {f.category}
                    </span>
                    <ArrowUpRight
                      size={13}
                      style={{ flex: '0 0 auto', color: C.textDim }}
                      aria-hidden="true"
                    />
                  </button>
                  {onAskArgo && (
                    <button
                      type="button"
                      className="glass-btn"
                      onClick={() => onAskArgo(findingQuery(f))}
                      title="Hand this to the Argo agent"
                      style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                    >
                      <Sparkles size={12} color={C.magentaSoft} /> Ask Argo
                    </button>
                  )}
                </li>
              );
            })}
            {scan.findings.length > 8 && (
              <li className="t-meta" style={{ paddingTop: 2 }}>
                + {scan.findings.length - 8} more
              </li>
            )}
          </ul>
        )}
      </GlassPanel>

      {/* Follow-ups — overdue + due-soon contacts from CRM followUpDate */}
      <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
        <div className="dash-section-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                display: 'inline-grid',
                placeItems: 'center',
                background: reminderCounts.overdue
                  ? 'linear-gradient(135deg, rgba(255,140,0,0.22), rgba(255,0,255,0.10))'
                  : 'linear-gradient(135deg, rgba(0,130,243,0.20), rgba(0,200,120,0.10))',
                border: '1px solid rgba(255,255,255,0.14)',
                color: reminderCounts.overdue ? C.orangeWarm : C.blueElectric,
              }}
            >
              <CalendarClock size={17} strokeWidth={2} />
            </span>
            <div>
              <span className="t-eyebrow">CRM</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                Follow-ups
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {reminderCounts.overdue > 0 && (
              <span className="glass-pill glass-pill--orange">
                {reminderCounts.overdue} overdue
              </span>
            )}
            {reminderCounts.today > 0 && (
              <span className="glass-pill glass-pill--magenta">
                {reminderCounts.today} today
              </span>
            )}
            {reminderCounts.soon > 0 && (
              <span className="glass-pill glass-pill--blue">
                {reminderCounts.soon} soon
              </span>
            )}
          </div>
        </div>

        {reminders.length === 0 ? (
          <div className="crm-empty" style={{ padding: '20px 0' }}>
            <CheckCircle2 size={20} strokeWidth={1.75} />
            <span>No follow-ups due — you're on top of the pipeline.</span>
          </div>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: '4px 0 0',
              padding: 0,
              display: 'grid',
              gap: 8,
            }}
          >
            {reminders.slice(0, 6).map((r) => (
              <FollowUpRow
                key={r.person.id}
                reminder={r}
                onClick={() => onNavigate?.('contacts')}
                canMutate={canMutate}
                onAskArgo={onAskArgo}
              />
            ))}
            {reminders.length > 6 && (
              <li className="t-meta" style={{ paddingTop: 2 }}>
                + {reminders.length - 6} more
              </li>
            )}
          </ul>
        )}
      </GlassPanel>

      {/* At-risk customers — live accounts that have slipped, riskiest first */}
      <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
        <div className="dash-section-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                display: 'inline-grid',
                placeItems: 'center',
                background: atRisk.some((r) => r.health.band === 'at-risk')
                  ? 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))'
                  : 'linear-gradient(135deg, rgba(0,200,120,0.20), rgba(0,130,243,0.10))',
                border: '1px solid rgba(255,255,255,0.14)',
                color: atRisk.some((r) => r.health.band === 'at-risk') ? C.magenta : C.green,
              }}
            >
              <HeartPulse size={17} strokeWidth={2} />
            </span>
            <div>
              <span className="t-eyebrow">Customer success</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                At-risk customers
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="glass-btn"
            onClick={() => onNavigate?.('customer-success')}
            title="Open the customer health cockpit"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            View all <ArrowUpRight size={13} />
          </button>
        </div>

        {atRisk.length === 0 ? (
          <div className="crm-empty" style={{ padding: '20px 0' }}>
            <ShieldCheck size={20} strokeWidth={1.75} />
            <span>Every live customer is healthy — nothing slipping.</span>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'grid', gap: 8 }}>
            {atRisk.slice(0, 5).map(({ person, health }) => {
              const dot: Record<HealthBand, string> = {
                'at-risk': C.magenta,
                watch: C.orange,
                healthy: C.green,
              };
              const pill: Record<HealthBand, string> = {
                'at-risk': 'glass-pill glass-pill--magenta',
                watch: 'glass-pill glass-pill--orange',
                healthy: 'glass-pill glass-pill--blue',
              };
              const topFactor = [...health.factors]
                .filter((f) => f.points < 0)
                .sort((a, b) => a.points - b.points)[0]?.label;
              return (
                <li key={person.id} style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
                  <button
                    type="button"
                    className="glass-row"
                    onClick={() => onNavigate?.('customer-success')}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        flex: '0 0 auto',
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: dot[health.band],
                        boxShadow: `0 0 8px ${dot[health.band]}`,
                      }}
                    />
                    <span
                      aria-hidden="true"
                      title={`Health ${health.score} / 100`}
                      style={{
                        flex: '0 0 auto',
                        width: 32,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 700,
                        color: dot[health.band],
                      }}
                    >
                      {health.score}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        className="t-card-title"
                        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {person.name}
                      </span>
                      <span
                        className="t-meta"
                        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {topFactor ?? 'Slipping'}
                      </span>
                    </span>
                    <span className={pill[health.band]} style={{ flex: '0 0 auto' }}>
                      {health.band === 'at-risk' ? 'At risk' : 'Watch'}
                    </span>
                  </button>
                </li>
              );
            })}
            {atRisk.length > 5 && (
              <li className="t-meta" style={{ paddingTop: 2 }}>
                + {atRisk.length - 5} more
              </li>
            )}
          </ul>
        )}
      </GlassPanel>

      {/* Funnel + activity */}
      <div className="dash-grid">
        {/* Phase funnel */}
        <GlassPanel variant="default" className="dash-funnel">
          <div className="dash-section-head">
            <div>
              <span className="t-eyebrow">Pipeline</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                Funnel by phase
              </h2>
            </div>
            <span className="t-meta">{stats.workflowsTotal} workflows total</span>
          </div>

          <div className="dash-funnel__rows">
            {(['sales', 'implementations', 'customer-success'] as PipelinePhase[]).map(
              (phase) => {
                const count =
                  phase === 'sales'
                    ? stats.inSales
                    : phase === 'implementations'
                      ? stats.inImpl
                      : stats.inCS;
                const meta = PHASE_META[phase];
                const pct =
                  stats.workflowsTotal === 0
                    ? 0
                    : Math.round((count / stats.workflowsTotal) * 100);
                return (
                  <div key={phase} className="dash-funnel__row">
                    <div className="dash-funnel__label">
                      <span
                        className="dash-funnel__dot"
                        style={{ background: meta.accent }}
                      />
                      <span>{meta.label}</span>
                    </div>
                    <div
                      className="dash-funnel__bar"
                      style={{ ['--bar-accent' as any]: meta.accent }}
                    >
                      <div
                        className="dash-funnel__fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="dash-funnel__count">
                      <span className="t-card-title">{count}</span>
                      <span className="t-meta"> · {pct}%</span>
                    </div>
                  </div>
                );
              },
            )}
          </div>

          {/* Stage breakdown */}
          <div className="dash-stages">
            {PIPELINE_STAGES.map((stage) => {
              const cnt = accounts.reduce(
                (n, a) =>
                  n + a.workflows.filter((w) => w.stage === stage).length,
                0,
              );
              if (cnt === 0) return null;
              const meta = PHASE_META[STAGE_PHASE[stage]];
              return (
                <span
                  key={stage}
                  className={meta.pill}
                  title={`${cnt} workflow${cnt === 1 ? '' : 's'} in ${stage}`}
                >
                  {stage}
                  <span className="dash-stages__count">{cnt}</span>
                </span>
              );
            })}
            {stats.workflowsTotal === 0 && (
              <span className="t-meta">No workflows yet — add an account.</span>
            )}
          </div>
        </GlassPanel>

        {/* Unified activity — every timeline (accounts, contacts, tickets,
            tasks, agent runs) in one stream, filterable by human/agent actor.
            This is both the team's shared "what happened" record and the
            public audit trail for agent work. */}
        <GlassPanel variant="default" className="dash-activity">
          <div className="dash-section-head">
            <div>
              <span className="t-eyebrow">Activity</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                Unified feed
              </h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {(['all', 'human', 'agent'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`glass-pill${feedFilter === f ? ' glass-pill--magenta' : ''}`}
                  onClick={() => setFeedFilter(f)}
                  style={{ cursor: 'pointer', border: 'none', textTransform: 'capitalize' }}
                  title={
                    f === 'all'
                      ? 'Everything, humans and agents'
                      : f === 'human'
                        ? 'Only human actors'
                        : 'Only agent actors (claude:*, argo)'
                  }
                >
                  {f === 'agent' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Bot size={11} /> Agents
                    </span>
                  ) : f === 'human' ? (
                    'Humans'
                  ) : (
                    'All'
                  )}
                </button>
              ))}
            </div>
          </div>
          {visibleFeed.length === 0 ? (
            <div className="crm-empty" style={{ padding: '24px 0' }}>
              <Inbox size={20} strokeWidth={1.75} />
              <span>
                {feedFilter === 'agent'
                  ? 'No agent activity yet — deploy one under Projects → Agents.'
                  : 'No activity yet.'}
              </span>
            </div>
          ) : (
            <ol className="dash-activity__list">
              {visibleFeed.map((e) => {
                const meta = FEED_SOURCE_META[e.source];
                const isAgentActor = AGENT_ACTOR.test(e.actor);
                return (
                  <li key={e.id} className="dash-activity__item">
                    <div className="dash-activity__dot" aria-hidden="true" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t-body" style={{ color: C.text }}>
                        <span style={{ fontWeight: 700 }}>{e.label}</span>
                        {' — '}
                        <span style={{ color: C.textMuted }}>{e.text}</span>
                      </div>
                      <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span className={meta.pill} style={{ fontSize: 10, padding: '1px 7px' }}>
                          {meta.label}
                        </span>
                        {fmtRelative(e.at)} ·{' '}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          {isAgentActor && <Bot size={10} aria-hidden="true" />}
                          {e.actor}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </GlassPanel>
      </div>

      {/* Your agents — compact fleet widget: live counts + recent ledgered
          runs, proving the deployed agents are real governed workers.
          Deep-links to Projects → Agents. */}
      <AgentFleetWidget
        onOpen={() => {
          onSelectProjectsTab?.('agents');
          onNavigate?.('projects');
        }}
      />

      {/* Quick links */}
      <GlassPanel variant="subtle" animate={false} className="dash-quick">
        <div className="dash-section-head" style={{ marginBottom: 0 }}>
          <div>
            <span className="t-eyebrow">Jump to</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              Common workflows
            </h2>
          </div>
        </div>
        <div className="dash-quick__grid">
          <QuickLink
            label="Accounts CRM"
            desc="POC, owner, stage rollup."
            icon={Building2}
            onClick={() => onNavigate?.('contacts')}
          />
          <QuickLink
            label="Projects"
            desc="Track delivery across clients."
            icon={FolderKanban}
            onClick={() => {
              onSelectProjectsTab?.('projects');
              onNavigate?.('projects');
            }}
          />
          <QuickLink
            label="Content Engine"
            desc="Draft on-brand assets."
            icon={TrendingUp}
            onClick={() => onNavigate?.('content-engine')}
          />
        </div>
      </GlassPanel>
    </div>
  );
}

/**
 * One card in the Dashboard pulse row — a full-card button that surfaces a
 * buried feature (health / goals / connections) and deep-links to its view.
 * Styled to match the KPI cards' glass treatment, with an explicit CTA pill
 * so the click-through destination is obvious.
 */
function PulseCard({
  icon: Icon,
  title,
  cta,
  accent,
  onClick,
  children,
  ...rest
}: {
  icon: LucideIcon;
  title: string;
  /** Destination name shown in the corner pill, e.g. "Goals". */
  cta: string;
  accent: string;
  onClick: () => void;
  children: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...rest}
      style={{
        appearance: 'none',
        fontFamily: "'Manrope', sans-serif",
        textAlign: 'left',
        cursor: 'pointer',
        display: 'grid',
        gap: 10,
        alignContent: 'start',
        padding: '16px 18px',
        borderRadius: 18,
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
        border: '1px solid rgba(255,255,255,0.08)',
        color: 'var(--argo-text)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          aria-hidden="true"
          style={{
            width: 30,
            height: 30,
            borderRadius: 9,
            display: 'inline-grid',
            placeItems: 'center',
            border: '1px solid rgba(255,255,255,0.14)',
            background: 'rgba(255,255,255,0.05)',
            color: accent,
            flex: '0 0 auto',
          }}
        >
          <Icon size={15} strokeWidth={2} />
        </span>
        <span className="t-section-title" style={{ flex: 1, minWidth: 0, margin: 0 }}>
          {title}
        </span>
        <span className="glass-pill" style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {cta} <ArrowUpRight size={11} aria-hidden="true" />
        </span>
      </span>
      <span style={{ display: 'block', minWidth: 0 }}>{children}</span>
    </button>
  );
}

function FollowUpRow({
  reminder,
  onClick,
  canMutate,
  onAskArgo,
}: {
  reminder: FollowUpReminder;
  onClick: () => void;
  canMutate: boolean;
  onAskArgo?: (query: string) => void;
}) {
  const { person, urgency } = reminder;
  // One-click resolution: push the follow-up 3 days out. The button states
  // exactly what it writes, so the click IS the confirmation — same trust
  // model as the chat's confirm cards, one step shorter.
  const [pushing, setPushing] = useState(false);
  const pushThreeDays = async () => {
    if (pushing) return;
    setPushing(true);
    try {
      await runAction({
        kind: 'schedule_follow_up',
        personId: person.id,
        personName: person.name,
        followUpDate: plusDaysIso(3),
        note: reminder.nextStep,
      });
      // The people subscription refreshes the list — the row removes itself.
    } finally {
      setPushing(false);
    }
  };
  const dot =
    urgency === 'overdue' ? C.orangeWarm : urgency === 'today' ? C.magenta : C.blueElectric;
  return (
    <li style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
      <button
        type="button"
        className="glass-row"
        onClick={onClick}
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 12px',
          cursor: 'pointer',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            flex: '0 0 auto',
            width: 8,
            height: 8,
            borderRadius: 999,
            background: dot,
            boxShadow: `0 0 8px ${dot}`,
          }}
        />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            className="t-card-title"
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {person.name}
            {person.company ? ` · ${person.company}` : ''}
          </span>
          <span
            className="t-meta"
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {reminder.nextStep ?? 'Follow up'}
          </span>
        </span>
        <span
          className={
            urgency === 'overdue'
              ? 'glass-pill glass-pill--orange'
              : urgency === 'today'
                ? 'glass-pill glass-pill--magenta'
                : 'glass-pill glass-pill--blue'
          }
          style={{ flex: '0 0 auto' }}
        >
          {reminderLabel(reminder)}
        </span>
        <ArrowUpRight
          size={13}
          style={{ flex: '0 0 auto', color: C.textDim }}
          aria-hidden="true"
        />
      </button>
      {canMutate && (
        <button
          type="button"
          className="glass-btn"
          onClick={() => void pushThreeDays()}
          disabled={pushing}
          title={`Reschedule this follow-up to ${plusDaysIso(3)}`}
          style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}
        >
          <CalendarClock size={12} /> {pushing ? '…' : '+3d'}
        </button>
      )}
      {onAskArgo && (
        <button
          type="button"
          className="glass-btn"
          onClick={() => onAskArgo(`Tell me about ${person.name}`)}
          title="Hand this contact to the Argo agent"
          style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}
        >
          <Sparkles size={12} color={C.magentaSoft} /> Ask Argo
        </button>
      )}
    </li>
  );
}

function QuickLink({
  label,
  desc,
  icon: Icon,
  onClick,
}: {
  label: string;
  desc: string;
  icon: LucideIcon;
  onClick: () => void;
}) {
  return (
    <button type="button" className="dash-quick__card" onClick={onClick}>
      <span className="dash-quick__icon" aria-hidden="true">
        <Icon size={16} strokeWidth={2} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="t-card-title">{label}</span>
        <span className="t-meta" style={{ display: 'block' }}>{desc}</span>
      </span>
      <ArrowUpRight size={14} className="dash-quick__chev" aria-hidden="true" />
    </button>
  );
}

function fmtRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 14) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
